const COS_BUCKET_HOST_RE = /^octo-[\w-]+-\d+\.cos\.[\w-]+\.myqcloud\.com$/i;
const DEFAULT_COS_CDN_HOSTS = ['cdn.dobidobi.cn', 'img.dobidobi.cn', 'media.dobidobi.cn'] as const;
const COS_PROXY_PREFIX = '/api/assets/proxy/download?url=';
const TEMPLATE_COVER_PATH_PREFIX = '/template-covers/';
const LOCAL_ASSET_PATH_PREFIXES = [
  '/api/assets/stream/',
  '/api/assets/proxy/download',
  TEMPLATE_COVER_PATH_PREFIX,
] as const;

function getConfiguredCosCdnHosts(): string[] {
  const configured = process.env.NEXT_PUBLIC_COS_CDN_HOSTS
    ?.split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);

  if (configured && configured.length > 0) {
    return configured;
  }

  return [...DEFAULT_COS_CDN_HOSTS];
}
const NEXT_IMAGE_BYPASS_PREFIXES = [
  '/_next/image?',
  COS_PROXY_PREFIX,
  '/api/assets/stream/',
  '/api/assets/proxy/download',
  'http://localhost:8000/api/assets/stream/',
  'http://127.0.0.1:8000/api/assets/stream/',
  'http://localhost:8000/api/assets/proxy/download',
  'http://127.0.0.1:8000/api/assets/proxy/download',
] as const;

export function unwrapNextImageOptimizationUrl(url?: string | null): string {
  if (!url) return '';

  let current = url.trim();
  for (let depth = 0; depth < 3; depth += 1) {
    try {
      const parsed = new URL(current, 'https://dobidobi.local');
      if (parsed.pathname !== '/_next/image') return current;
      const source = parsed.searchParams.get('url');
      if (!source || source === current) return current;
      current = source;
    } catch {
      return current;
    }
  }
  return current;
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function hasLocalAssetPath(pathname: string): boolean {
  return LOCAL_ASSET_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function shouldNormalizeDisplayUrl(parsed: URL): boolean {
  if (!hasLocalAssetPath(parsed.pathname)) return false;

  if (parsed.pathname.startsWith('/api/assets/')) {
    return true;
  }

  if (isLoopbackHost(parsed.hostname)) {
    return true;
  }

  return typeof window !== 'undefined' && parsed.origin === window.location.origin;
}

export function isCosBucketUrl(url?: string | null): boolean {
  if (!url) return false;

  try {
    const parsed = /^https?:\/\//i.test(url) ? new URL(url) : new URL(url, 'https://octolab.local');
    return isDirectCosBucketUrl(parsed.toString())
      || getConfiguredCosCdnHosts().includes(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function isDirectCosBucketUrl(url?: string | null): boolean {
  if (!url) return false;

  try {
    const parsed = /^https?:\/\//i.test(url) ? new URL(url) : new URL(url, 'https://octolab.local');
    return COS_BUCKET_HOST_RE.test(parsed.hostname);
  } catch {
    return false;
  }
}

export function isTemplateCoverAssetUrl(url?: string | null): boolean {
  if (!url) return false;
  if (url.startsWith(TEMPLATE_COVER_PATH_PREFIX)) return true;

  try {
    const parsed = /^https?:\/\//i.test(url) ? new URL(url) : new URL(url, 'https://octolab.local');
    return parsed.pathname.startsWith(TEMPLATE_COVER_PATH_PREFIX);
  } catch {
    return url.includes(TEMPLATE_COVER_PATH_PREFIX);
  }
}

export function toDisplayMediaUrl(url?: string | null): string {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url)) return url;

  try {
    const parsed = new URL(url);
    if (!shouldNormalizeDisplayUrl(parsed)) return url;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

export function toProxyMediaUrl(url?: string | null): string {
  if (!url) return '';
  if (url.startsWith(COS_PROXY_PREFIX)) return url;
  if (!isCosBucketUrl(url)) return url;
  return `${COS_PROXY_PREFIX}${encodeURIComponent(url)}`;
}

export function toAbsoluteMediaUrl(url?: string | null): string {
  if (!url) return '';
  if (/^(?:blob:|data:|https?:\/\/)/i.test(url)) return url;
  if (typeof window === 'undefined') return url;

  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

export function shouldBypassNextImageOptimization(url?: string | null): boolean {
  const displayUrl = toDisplayMediaUrl(url);
  if (!displayUrl) return false;
  if (displayUrl.startsWith('blob:') || displayUrl.startsWith('data:')) return true;
  if (isCosBucketUrl(displayUrl)) return true;
  if (isTemplateCoverAssetUrl(displayUrl)) return true;
  return NEXT_IMAGE_BYPASS_PREFIXES.some((prefix) => displayUrl.startsWith(prefix));
}
